
import { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNotificationForm } from "./useNotificationForm";
import { createNotificationInDb } from "@/services/notificationService";
import { createBulkNotification } from "@/services/bulkNotificationService";
import { BulkOrderEntry } from "@/hooks/useBulkOrderProducts";

import { useIsMobile } from "@/hooks/use-mobile";

export const useCreateNotification = (userId: string | undefined, userName: string | undefined, onSuccess: () => void) => {
  
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  // Use ref so mutationFn always sees the latest value without re-creating the mutation
  const bulkEntriesRef = useRef<BulkOrderEntry[] | null>(null);
  
  const {
    notificationForm: newNotification,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    
    handleToggleOrderProduct,
    handleOrderProductMessageChange,
    handleOverarchingChange,
  } = useNotificationForm(userId, userName);

  const createNotificationMutation = useMutation({
    mutationFn: async () => {
      console.log("Starting notification creation with form data:", newNotification);
      
      const pendingBulk = bulkEntriesRef.current;
      
      // Check if this is a bulk submission
      if (pendingBulk && pendingBulk.length > 0) {
        console.log("Bulk submission detected with", pendingBulk.length, "entries");
        const validEntries = pendingBulk.filter(e => !e.error && e.products.length > 0);
        
        if (validEntries.length === 0) {
          throw new Error("Geen geldige orders om te versturen");
        }

        // Apply form-level type/status to entries that don't have one
        const enrichedEntries = validEntries.map(e => ({
          ...e,
          type: e.type || newNotification.type,
          status: e.status || newNotification.status,
          message: e.message || newNotification.message,
        }));

        const result = await createBulkNotification(
          enrichedEntries,
          newNotification.userId,
          newNotification.userName
        );
        
        if (!result.success) {
          throw new Error(result.error || "Bulk aanmaken mislukt");
        }
        
        return result;
      }
      
      return createNotificationInDb(newNotification);
    },
    onSuccess: (data) => {
      console.log("Notification created successfully:", data);
      
      queryClient.invalidateQueries({ queryKey: ['notifications', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-count', 'unread'] });
      
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['notifications', 'active'] });
        queryClient.refetchQueries({ queryKey: ['notifications-count', 'active'] });
        queryClient.refetchQueries({ queryKey: ['notifications-count', 'unread'] });
      }, 100);
      
      const bulkCount = bulkEntriesRef.current?.filter(e => !e.error && e.products.length > 0).length;
      toast.success(bulkCount ? `Melding aangemaakt voor ${bulkCount} orders` : "Melding aangemaakt");
      
      bulkEntriesRef.current = null;
      
      if (isMobile) {
        document.body.classList.remove('form-submitting');
      }
      
      onSuccess();
    },
    onError: (error: any) => {
      console.error("Error in createNotification mutation:", error);
      
      bulkEntriesRef.current = null;
      
      if (isMobile) {
        document.body.classList.remove('form-submitting');
      }
      
      toast.error("Fout bij het aanmaken van melding", {
        description: error.message
      });
    }
  });

  useEffect(() => {
    return () => {
      document.body.classList.remove('form-submitting');
    };
  }, []);

  const handleSubmit = (e: React.FormEvent, bulkEntries?: BulkOrderEntry[]) => {
    e.preventDefault();
    
    console.log("Form submitted with data:", newNotification, "bulk entries:", bulkEntries?.length || 0);
    
    bulkEntriesRef.current = bulkEntries && bulkEntries.length > 0 ? bulkEntries : null;
    createNotificationMutation.mutate();
  };

  return {
    newNotification,
    isPending: createNotificationMutation.isPending,
    handleInputChange,
    handleSelectChange,
    handleCheckboxChange,
    handleInternalCheckboxChange,
    toggleExternalRecipient,
    handleSubmit,
    handleToggleOrderProduct,
    handleOrderProductMessageChange,
    handleOverarchingChange,
  };
};
